import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { formatClassNames, updateCurrency } from "../../../utils/common";
import isEmpty from "lodash.isempty";
import ProductTagFlipper from "../../molecules/productTagFlipper";
import RateAndReview from "../../molecules/rateAndReview";
import EarliestDeliveryDate from "../earliestDeliveryDate";
import { usePathname } from "next/navigation";
import { getProductDeliveryDisplay } from "../../../utils/plp/serviceabilityDelivery";

/**
 * Product card metadata: title, tags, review, price, and delivery row.
 *
 * @param {Object} props
 * @returns {JSX.Element}
 */
const ProductInfo = ({
  title,
  price,
  isHideSavedPercent = false,
  review,
  isSmallReviewTag,
  productAttributes,
  isMobile,
  deliveryEntry,
  earliestDelivery,
  pincode,
  isPlpRevampTiles,
  serverSelectedCurrency,
  isDeliveryTimeHidden = false,
}) => {
  const selectedCurrencyFromState = useSelector((state) => state?.currencyList?.userSelectedCurrency);
  const selectedCurrency = serverSelectedCurrency || selectedCurrencyFromState;
  const hasProductAttributes = productAttributes?.length > 0;
  const hasReview = (review && review?.ratingCount > 100) || (review && review?.reviewCount > 100);
  const showTagReviewRow = hasProductAttributes || hasReview;
  const pathName = usePathname();
  const isPLP = pathName?.includes("-lp");
  const isSearch = pathName?.includes("/search");
  const resolvedDeliveryEntry = deliveryEntry ?? earliestDelivery;
  const showDeliverySection = Boolean(pincode) && Boolean(getProductDeliveryDisplay(resolvedDeliveryEntry));

  return (
    <div className="pt-8 sm:p-12">
      {title && (
        <div
          className={formatClassNames(
            isMobile ? "line-clamp-2" : "line-clamp-1",
            "mb-4 text-12 font-500 leading-0 text-fnp-500 md:text-14",
            (isPLP || isSearch) && isPlpRevampTiles && "xxl:text-16",
          )}
          itemProp="name"
        >
          {title}
        </div>
      )}

      {showTagReviewRow && (
        <div
          className={formatClassNames(
            "mb-4 flex w-full min-w-0 items-center",
            hasProductAttributes && hasReview ? "justify-between" : "justify-start",
          )}
        >
          {hasProductAttributes && (
            <div className={`flex min-w-0 ${hasReview ? "max-w-[65%] shrink" : "shrink"}`}>
              <ProductTagFlipper productAttributes={productAttributes} isMobile={isMobile} />
            </div>
          )}
          {hasReview && (
            <div
              className={formatClassNames(
                "flex min-w-0 shrink overflow-hidden",
                hasProductAttributes ? "max-w-[35%] justify-end" : "justify-start",
              )}
            >
              <RateAndReview
                review={review}
                isReviewTextHide={true}
                isSmallReviewTag={isSmallReviewTag}
                isSchema={false}
                classes="static"
                isPadding={false}
                isTruncate={true}
                isProductListing={(isPLP || isSearch) && isPlpRevampTiles}
              />
            </div>
          )}
        </div>
      )}

      {price && (
        <>
          <meta itemProp="priceCurrency" content={isEmpty(selectedCurrency) ? "INR" : selectedCurrency?.currency?.id} />
          <div className="priceDetails mb-4 flex flex-wrap items-center gap-4 md:mb-6">
            <span
              className={formatClassNames(
                price?.percentSaved > 0 && "ml-4",
                "text-14 font-600",
                (isPLP || isSearch) && isPlpRevampTiles && "xxl:text-18",
              )}
            >
              {updateCurrency(selectedCurrency, price?.price ? price?.price : price?.listPrice)}
            </span>
            {price?.percentSaved > 0 && (
              <span className="overflow-hidden text-ellipsis whitespace-nowrap text-12 font-400 text-grey-400 line-through">
                {updateCurrency(
                  selectedCurrency,
                  price?.mrp ? price?.mrp : price?.listPrice,
                  false,
                  false,
                  true,
                  false,
                )}
              </span>
            )}

            {!isHideSavedPercent && price?.percentSaved > 0 && (
              <span
                className={formatClassNames(
                  "w-fit text-10 font-700 text-offer-900",
                  (isPLP || isSearch) && isPlpRevampTiles && "xxl:text-12",
                )}
                style={{
                  clipPath: "polygon(0 100%, 90% 100%, 100% 0, 0% 0)",
                }}
              >
                {Math.round(price?.percentSaved)}% OFF
              </span>
            )}
          </div>
        </>
      )}

      {showDeliverySection && (
        <div className="mb-8">
          <EarliestDeliveryDate deliveryEntry={resolvedDeliveryEntry} isDeliveryTimeHidden={isDeliveryTimeHidden} />
        </div>
      )}
    </div>
  );
};

ProductInfo.propTypes = {
  title: PropTypes.string.isRequired,
  price: PropTypes.shape({
    price: PropTypes.number,
    listPrice: PropTypes.number,
    mrp: PropTypes.number,
    percentSaved: PropTypes.number,
  }).isRequired,
  isHideSavedPercent: PropTypes.bool,
  review: PropTypes.object,
  isSmallReviewTag: PropTypes.bool,
  productAttributes: PropTypes.arrayOf(PropTypes.object),
  isMobile: PropTypes.bool.isRequired,
  deliveryEntry: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  earliestDelivery: PropTypes.string,
  pincode: PropTypes.string,
  isPlpRevampTiles: PropTypes.bool,
  serverSelectedCurrency: PropTypes.object,
  isDeliveryTimeHidden: PropTypes.bool,
};

export default React.memo(ProductInfo);
